import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, CheckCircle } from 'lucide-react';
import { captureError } from '@/lib/sentry';

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestType: 'demo' | 'trial';
}

// API endpoint - base URL without path
const CONTACT_API_BASE = import.meta.env.REACT_APP_CONTACT_API_URL || '';
const API_ENDPOINT = `${CONTACT_API_BASE}/contact`;

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({ isOpen, onClose, requestType }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          requestType: requestType === 'demo' ? 'Demo Request' : 'Free Trial Request',
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.error || t('freeTrial.errorGeneric'));
      }
    } catch (err) {
      captureError(err as Error, {
        component: 'DemoRequestModal',
        action: 'form_submit',
        requestType,
        endpoint: API_ENDPOINT,
      });
      setError(t('freeTrial.errorNetwork'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', company: '', message: '' });
    setIsSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>

        {isSubmitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('freeTrial.modalThankYou')}</h3>
            <p className="text-slate-600 mb-6">
              {t('freeTrial.modalResponseMessage')}
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors"
            >
              {t('freeTrial.modalClose')}
            </button>
          </div>
        ) : (
          <div className="p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              {requestType === 'demo' ? t('freeTrial.modalRequestDemo') : t('freeTrial.modalStartTrial')}
            </h3>
            <p className="text-slate-600 mb-6">
              {requestType === 'demo'
                ? t('freeTrial.modalDemoSubtitle')
                : t('freeTrial.modalTrialSubtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                  {t('freeTrial.labelFullName')}
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('freeTrial.placeholderFullName')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                  {t('freeTrial.labelWorkEmail')}
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('freeTrial.placeholderWorkEmail')}
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-1">
                  {t('freeTrial.labelCompany')}
                </label>
                <input
                  type="text"
                  id="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('freeTrial.placeholderCompany')}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                  {t('freeTrial.labelMessage')}
                </label>
                <textarea
                  id="message"
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder={t('freeTrial.placeholderMessage')}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  t('freeTrial.sending')
                ) : (
                  <>
                    <Send size={20} />
                    {requestType === 'demo' ? t('freeTrial.submitRequestDemo') : t('freeTrial.submitStartTrial')}
                  </>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRequestModal;
